/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { StatCardV2 } from '../../../components/v2';
import ChartFocusDialog from '../RelatorioDigitacao/ChartFocusDialog';
import {
  formatBRL,
  formatPercent,
  getTopRanking,
  RANKING_TOP_N,
} from './constants';
import { ChartCard, FOCUS_CHART_HEIGHT } from './chartShared';
import SegmentComparisonChart from './SegmentComparisonChart';
import SegmentComparisonTable from './SegmentComparisonTable';
import UsuarioRankingChart from './UsuarioRankingChart';

const PODIUM_COLORS = ['primary', 'default', 'default'];
const PODIUM_LABELS = ['#1', '#2', '#3'];

const ParetoInsight = ({ insight, loading }) => {
  if (loading) {
    return <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2 }} />;
  }

  const { topCount, topPercent, totalUsers, totalGasto } = insight;

  if (totalUsers === 0) return null;

  const userWord = topCount === 1 ? 'colaborador' : 'colaboradores';
  const allWord = totalUsers === 1 ? 'colaborador' : 'colaboradores';

  return (
    <Alert
      severity="info"
      variant="outlined"
      icon={<InsightsOutlinedIcon />}
      sx={{ borderRadius: 2, alignItems: 'flex-start' }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Top {topCount} {userWord} concentram {formatPercent(topPercent)} do gasto total
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {formatBRL(totalGasto)} em {totalUsers} {allWord} no período — clique em um nome para ver o detalhamento
      </Typography>
    </Alert>
  );
};

ParetoInsight.propTypes = {
  insight: PropTypes.shape({
    topCount: PropTypes.number,
    topPercent: PropTypes.number,
    totalUsers: PropTypes.number,
    totalGasto: PropTypes.number,
  }).isRequired,
  loading: PropTypes.bool,
};

ParetoInsight.defaultProps = {
  loading: false,
};

const Top3Podium = ({ ranking, totalGasto, loading, onSelectUsuario }) => {
  const top3 = useMemo(() => (ranking || []).slice(0, 3), [ranking]);

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2].map((i) => (
          <Grid key={i} size={{ xs: 12, sm: 4 }}>
            <Skeleton variant="rounded" height={100} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (top3.length === 0) return null;

  return (
    <Grid container spacing={2}>
      {top3.map((item, index) => {
        const percent = totalGasto > 0 ? (item.total / totalGasto) * 100 : 0;
        return (
          <Grid key={item.usuarioId || item.nome} size={{ xs: 12, sm: 4 }}>
            <StatCardV2
              label={`${PODIUM_LABELS[index]} · ${item.nome}`}
              value={formatBRL(item.total)}
              icon={index === 0 ? <EmojiEventsIcon /> : <LeaderboardOutlinedIcon />}
              color={PODIUM_COLORS[index]}
              valueFontSize="1.25rem"
              onClick={() => onSelectUsuario?.(item.usuarioId, item.nome)}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, pl: 0.5 }}
            >
              {formatPercent(percent)} do gasto do recorte
            </Typography>
          </Grid>
        );
      })}
    </Grid>
  );
};

Top3Podium.propTypes = {
  ranking: PropTypes.arrayOf(PropTypes.object),
  totalGasto: PropTypes.number,
  loading: PropTypes.bool,
  onSelectUsuario: PropTypes.func,
};

Top3Podium.defaultProps = {
  ranking: [],
  totalGasto: 0,
  loading: false,
  onSelectUsuario: null,
};

const RankingTable = ({ ranking, totalGasto, loading, onSelectUsuario }) => {
  const rows = useMemo(() => getTopRanking(ranking), [ranking]);

  if (loading) {
    return <Skeleton variant="rounded" height={280} />;
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        Sem colaboradores com gasto no período.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Colaborador</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>%</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
              Aporte
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
              Cartão
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' } }}>
              Notas
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const percent = totalGasto > 0 ? (row.total / totalGasto) * 100 : 0;
            return (
              <TableRow
                key={row.usuarioId || row.nome}
                hover
                onClick={() => onSelectUsuario?.(row.usuarioId, row.nome)}
                sx={{ cursor: onSelectUsuario ? 'pointer' : 'default' }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.nome}</TableCell>
                <TableCell align="right">{formatBRL(row.total)}</TableCell>
                <TableCell align="right">{formatPercent(percent)}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {formatBRL(row.viaAporte)}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {formatBRL(row.viaCartao)}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  {row.quantidadeNotas ?? 0}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

RankingTable.propTypes = {
  ranking: PropTypes.arrayOf(PropTypes.object),
  totalGasto: PropTypes.number,
  loading: PropTypes.bool,
  onSelectUsuario: PropTypes.func,
};

RankingTable.defaultProps = {
  ranking: [],
  totalGasto: 0,
  loading: false,
  onSelectUsuario: null,
};

const SegmentComparisonBlock = ({ segmentItems, totalGasto, loading, onSelectSegment }) => {
  const [focusChart, setFocusChart] = useState(false);
  const hasContent = loading || (segmentItems || []).length > 0;

  if (!hasContent) return null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
        <CompareArrowsOutlinedIcon fontSize="small" color="action" />
        <Typography variant="h6">Comparativo de Gastos por Segmento</Typography>
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <ChartCard title="Gastos por segmento" onExpand={() => setFocusChart(true)}>
            <SegmentComparisonChart
              items={segmentItems}
              loading={loading}
              onSelectSegment={onSelectSegment}
            />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <ChartCard title="Detalhamento">
            <SegmentComparisonTable
              items={segmentItems}
              totalGasto={totalGasto}
              loading={loading}
              onSelectSegment={onSelectSegment}
            />
          </ChartCard>
        </Grid>
      </Grid>

      <ChartFocusDialog
        open={focusChart}
        title="Comparativo de Gastos por Segmento"
        onClose={() => setFocusChart(false)}
      >
        <SegmentComparisonChart
          items={segmentItems}
          loading={loading}
          height={FOCUS_CHART_HEIGHT}
          onSelectSegment={onSelectSegment}
        />
      </ChartFocusDialog>
    </Box>
  );
};

SegmentComparisonBlock.propTypes = {
  segmentItems: PropTypes.arrayOf(PropTypes.object),
  totalGasto: PropTypes.number,
  loading: PropTypes.bool,
  onSelectSegment: PropTypes.func,
};

SegmentComparisonBlock.defaultProps = {
  segmentItems: [],
  totalGasto: 0,
  loading: false,
  onSelectSegment: null,
};

const UsuarioRankingBlock = ({
  ranking,
  paretoInsight,
  totalGasto,
  loading,
  onSelectUsuario,
}) => {
  const [focusChart, setFocusChart] = useState(false);
  const hasContent = loading || (ranking || []).length > 0;

  if (!hasContent) return null;

  const handleSelectFromChart = (usuarioId, nome) => {
    onSelectUsuario?.(usuarioId, nome);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
        <LeaderboardOutlinedIcon fontSize="small" color="action" />
        <Typography variant="h6">Ranking de Gastos por Colaborador</Typography>
        <Typography variant="body2" color="text.secondary">
          — Top {RANKING_TOP_N}
        </Typography>
      </Stack>

      <Stack spacing={2}>
        <ParetoInsight insight={paretoInsight} loading={loading} />
        <Top3Podium
          ranking={ranking}
          totalGasto={totalGasto}
          loading={loading}
          onSelectUsuario={onSelectUsuario}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <ChartCard title="Gastos por colaborador" onExpand={() => setFocusChart(true)}>
              <UsuarioRankingChart
                ranking={ranking}
                loading={loading}
                onSelectUsuario={handleSelectFromChart}
              />
            </ChartCard>
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            <ChartCard title="Detalhamento">
              <RankingTable
                ranking={ranking}
                totalGasto={totalGasto}
                loading={loading}
                onSelectUsuario={onSelectUsuario}
              />
            </ChartCard>
          </Grid>
        </Grid>
      </Stack>

      <ChartFocusDialog
        open={focusChart}
        title="Ranking de Gastos por Colaborador"
        onClose={() => setFocusChart(false)}
      >
        <UsuarioRankingChart
          ranking={ranking}
          loading={loading}
          height={FOCUS_CHART_HEIGHT}
          onSelectUsuario={handleSelectFromChart}
        />
      </ChartFocusDialog>
    </Box>
  );
};

UsuarioRankingBlock.propTypes = {
  ranking: PropTypes.arrayOf(PropTypes.object),
  paretoInsight: PropTypes.shape({
    topCount: PropTypes.number,
    topPercent: PropTypes.number,
    totalUsers: PropTypes.number,
    totalGasto: PropTypes.number,
  }).isRequired,
  totalGasto: PropTypes.number,
  loading: PropTypes.bool,
  onSelectUsuario: PropTypes.func,
};

UsuarioRankingBlock.defaultProps = {
  ranking: [],
  totalGasto: 0,
  loading: false,
  onSelectUsuario: null,
};

const UsuarioRankingSection = ({
  showSegmentComparison,
  showUsuarioRanking,
  ranking,
  segmentItems,
  paretoInsight,
  totalGasto,
  loading,
  onSelectUsuario,
  onSelectSegment,
}) => {
  const hasSegmentBlock = showSegmentComparison && (loading || (segmentItems || []).length > 0);
  const hasRankingBlock = showUsuarioRanking && (loading || (ranking || []).length > 0);

  if (!hasSegmentBlock && !hasRankingBlock) return null;

  return (
    <Stack spacing={4}>
      {hasSegmentBlock && (
        <SegmentComparisonBlock
          segmentItems={segmentItems}
          totalGasto={totalGasto}
          loading={loading}
          onSelectSegment={onSelectSegment}
        />
      )}
      {hasRankingBlock && (
        <UsuarioRankingBlock
          ranking={ranking}
          paretoInsight={paretoInsight}
          totalGasto={totalGasto}
          loading={loading}
          onSelectUsuario={onSelectUsuario}
        />
      )}
    </Stack>
  );
};

UsuarioRankingSection.propTypes = {
  showSegmentComparison: PropTypes.bool,
  showUsuarioRanking: PropTypes.bool,
  ranking: PropTypes.arrayOf(PropTypes.object),
  segmentItems: PropTypes.arrayOf(PropTypes.object),
  paretoInsight: PropTypes.shape({
    topCount: PropTypes.number,
    topPercent: PropTypes.number,
    totalUsers: PropTypes.number,
    totalGasto: PropTypes.number,
  }).isRequired,
  totalGasto: PropTypes.number,
  loading: PropTypes.bool,
  onSelectUsuario: PropTypes.func,
  onSelectSegment: PropTypes.func,
};

UsuarioRankingSection.defaultProps = {
  showSegmentComparison: false,
  showUsuarioRanking: true,
  ranking: [],
  segmentItems: [],
  totalGasto: 0,
  loading: false,
  onSelectUsuario: null,
  onSelectSegment: null,
};

export default UsuarioRankingSection;
